import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { SITE, SERVICES } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_INFO = [
  {
    icon: Phone,
    label: "Call Me",
    value: SITE.phone,
    href: SITE.phoneHref,
    testid: "contact-phone-link",
  },
  {
    icon: Mail,
    label: "Email Me",
    value: SITE.email,
    href: `mailto:${SITE.email}`,
    testid: "contact-email-link",
  },
  {
    icon: MapPin,
    label: "Location",
    value: SITE.location,
    href: null,
    testid: "contact-location",
  },
];

const EMPTY = { name: "", email: "", phone: "", website_type: SERVICES[0].title, message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || "Request sent successfully!");
      setForm(EMPTY);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send. Please call or email me directly.";
      toast.error(typeof msg === "string" ? msg : "Could not send. Please call or email me directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-all duration-300 focus:border-indigo-400/60 focus:bg-white/[0.06] focus:shadow-[0_0_0_3px_rgba(129,140,248,0.15)]";

  return (
    <section
      id="contact"
      className="relative scroll-mt-24 py-24 sm:py-28"
      data-testid="contact-section"
    >
      <div
        className="pointer-events-none absolute bottom-0 left-1/2 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-indigo-600/[0.1] blur-[130px]"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          center
          eyebrow="Get In Touch"
          title={
            <>
              Let&apos;s Build <span className="text-gradient">Your Website</span>
            </>
          }
          sub="Tell me about your business and I will get back to you with a plan — usually within a day."
        />

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
          {/* contact info cards */}
          <div className="flex flex-col gap-4">
            {CONTACT_INFO.map((c, i) => {
              const Icon = c.icon;
              const inner = (
                <>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-indigo-400/25 bg-gradient-to-br from-indigo-500/20 to-purple-500/10 text-indigo-300 transition-all duration-500 group-hover:shadow-[0_0_22px_rgba(129,140,248,0.45)]">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                      {c.label}
                    </p>
                    <p className="mt-1 break-words text-[15px] font-semibold text-white">
                      {c.value}
                    </p>
                  </div>
                </>
              );
              return (
                <Reveal key={c.label} delay={0.07 * i}>
                  {c.href ? (
                    <a
                      href={c.href}
                      data-testid={c.testid}
                      className="glass card-hover group flex items-center gap-4 rounded-2xl p-5"
                    >
                      {inner}
                    </a>
                  ) : (
                    <div data-testid={c.testid} className="glass flex items-center gap-4 rounded-2xl p-5">
                      {inner}
                    </div>
                  )}
                </Reveal>
              );
            })}

            <Reveal delay={0.3}>
              <div className="glass relative overflow-hidden rounded-2xl p-6" data-testid="contact-availability">
                <div
                  className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-purple-500/15 blur-2xl"
                  aria-hidden="true"
                />
                <p className="font-display text-lg font-bold text-white">
                  Have an idea? Let&apos;s make it live.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  I build, publish and deliver complete websites for businesses
                  of every category — so you can start attracting customers
                  online as soon as possible.
                </p>
              </div>
            </Reveal>
          </div>

          {/* form */}
          <Reveal delay={0.12}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="glass-strong rounded-3xl p-6 sm:p-8"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Name
                  </label>
                  <input
                    id="cf-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    data-testid="contact-name-input"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Email
                  </label>
                  <input
                    id="cf-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    data-testid="contact-email-input"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="cf-phone" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Phone
                  </label>
                  <input
                    id="cf-phone"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Your phone number"
                    data-testid="contact-phone-input"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="cf-type" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Website Type
                  </label>
                  <select
                    id="cf-type"
                    value={form.website_type}
                    onChange={set("website_type")}
                    data-testid="contact-type-select"
                    className={`${inputCls} appearance-none [&>option]:bg-[#0b0d15]`}
                  >
                    {SERVICES.map((s) => (
                      <option key={s.title} value={s.title}>
                        {s.title}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="cf-message" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Message
                </label>
                <textarea
                  id="cf-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell me about your business and what you need…"
                  data-testid="contact-message-input"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="btn-primary mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full px-7 py-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Start a Project <Send className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
